import { ExternalLink, RefreshCw } from "lucide-react";

import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";
import {
  buildSdTestAppLocalUrl,
  SDTESTAPP_DEFAULT_PORT,
  type SdTestConnectionStatus,
  type SdTestStatus,
} from "@/lib/sdtestapp/sdTestAppHttpClient";

function statusTone(connection: SdTestConnectionStatus, activeCount: number): BadgeTone {
  if (connection !== "connected") return "danger";
  if (activeCount === 1) return "success";
  return "warning";
}

function deviceBadgeTone(activeCount: number): BadgeTone {
  if (activeCount === 1) return "success";
  return "warning";
}

function connectionBadgeLabel(connection: SdTestConnectionStatus): string {
  if (connection === "connected") return "Connected";
  if (connection === "connecting") return "Connecting";
  return "Offline";
}

export function ConfigurationSdTestConnectionPanel({
  port,
  onPortChange,
  connection,
  activeCount,
  onReconnect,
}: {
  port: string;
  onPortChange: (port: string) => void;
  status?: SdTestStatus;
  connection: SdTestConnectionStatus;
  activeCount: number;
  onReconnect: () => void;
}): JSX.Element {
  const localUrl = buildSdTestAppLocalUrl(port);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
          SDTestApp
          <a
            href={localUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex rounded p-0.5 text-slate-500 hover:bg-slate-200/80 hover:text-slate-800"
            title={`Open ${localUrl}`}
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
            <span className="sr-only">Open SDTestApp</span>
          </a>
        </span>
        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <Badge tone={statusTone(connection, activeCount)} className="text-xs">
            {connectionBadgeLabel(connection)}
          </Badge>
          {connection === "connected" ? (
            <Badge tone={deviceBadgeTone(activeCount)} className="min-w-[1.5rem] justify-center text-xs tabular-nums">
              {activeCount}
            </Badge>
          ) : null}
        </div>
      </div>
      <div className="flex items-end gap-2">
        <div className="w-20">
          <Label htmlFor="sdtest-port" className="text-xs">
            Port
          </Label>
          <Input
            id="sdtest-port"
            value={port}
            inputMode="numeric"
            className="h-9 bg-white"
            onChange={(event) => onPortChange(event.target.value.trim() || SDTESTAPP_DEFAULT_PORT)}
          />
        </div>
        <Button type="button" variant="secondary" size="sm" className="h-9 bg-white" onClick={onReconnect} title="Reconnect">
          <RefreshCw className="h-4 w-4" aria-hidden />
          <span className="sr-only">Reconnect</span>
        </Button>
      </div>
    </div>
  );
}
